import { useEffect, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { Check, ArrowLeft } from "lucide-react";
import { CtaButton } from "@/components/site/CtaButton";
import { SectionHeading, Eyebrow } from "@/components/site/Sections";
import { FieldServiceCard, priceLabel, requestLink } from "@/components/field/FieldServiceCard";
import { RequestForm } from "@/components/field/RequestForm";
import { ServiceAreaMap } from "@/components/field/FieldSections";
import { getFieldServices, getFieldService } from "@/lib/api";
import { FIELD_CATEGORIES, catName } from "@/data/fieldServices";

export function FieldCategory() {
  const { category } = useParams();
  const c = FIELD_CATEGORIES.find((x) => x.id === category);
  const [services, setServices] = useState([]);
  useEffect(() => {
    getFieldServices().then(setServices).catch(() => setServices([]));
  }, []);
  if (!c) return <Navigate to="/field-services" replace />;
  const rows = services.filter((s) => s.category === c.id);
  return (
    <div data-testid={`fs-category-page-${c.id}`}>
      <section className="relative overflow-hidden bg-[#0B132B] text-white">
        <img src={c.image} alt="" className="absolute inset-0 h-full w-full object-cover opacity-25" />
        <div className="relative mx-auto max-w-7xl px-4 py-16 lg:px-8 lg:py-24">
          <Link to="/field-services" className="inline-flex items-center gap-2 text-sm text-slate-300 hover:text-gold"><ArrowLeft className="h-4 w-4" /> All field services</Link>
          <h1 data-testid="fs-category-title" className="mt-4 max-w-3xl font-display text-4xl font-extrabold sm:text-5xl">{c.name}</h1>
          <p className="mt-5 max-w-3xl text-base leading-relaxed text-slate-200">{c.description}</p>
          <CtaButton href="#request" size="lg" className="mt-8" data-testid="fs-category-request">Request Service</CtaButton>
        </div>
      </section>
      <section className="py-16">
        <div className="mx-auto max-w-7xl px-4 lg:px-8">
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {c.benefits.map((b) => <div key={b} className="flex gap-2 rounded-xl border border-slate-200 bg-white p-4 text-sm font-medium text-[#0B132B]"><Check className="h-4 w-4 shrink-0 text-gold" /> {b}</div>)}
          </div>
          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">{rows.map((s) => <FieldServiceCard key={s.id} s={s} />)}</div>
        </div>
      </section>
      <section id="request" className="bg-[#F5F3EC] py-16">
        <div className="mx-auto max-w-4xl px-4 lg:px-8">
          <SectionHeading eyebrow="Request a quote" title={`Request ${c.name}`} />
          <div className="mt-8"><RequestForm category={c.id} /></div>
        </div>
      </section>
    </div>
  );
}

export function FieldServiceDetail() {
  const { id } = useParams();
  const [s, setS] = useState(undefined);
  useEffect(() => {
    getFieldService(id).then(setS).catch(() => setS(null));
  }, [id]);
  if (s === null) return <Navigate to="/field-services" replace />;
  if (!s) return <div className="min-h-[50vh]" />;
  return (
    <section className="bg-[#F5F3EC] py-16 lg:py-24" data-testid="fs-detail-page">
      <div className="mx-auto grid max-w-6xl gap-10 px-4 lg:grid-cols-[1.4fr_1fr] lg:px-8">
        <div>
          <Link to={`/field-services/${s.category}`} className="text-sm text-slate-500 hover:text-gold">{catName(s.category)}</Link>
          <h1 data-testid="fs-detail-title" className="mt-3 font-display text-4xl font-extrabold text-[#0B132B] sm:text-5xl">{s.name}</h1>
          <p className="mt-6 text-base leading-relaxed text-slate-600">{s.description}</p>
          <Eyebrow className="mt-10 block">What's included</Eyebrow>
          <ul className="mt-4 grid gap-2 sm:grid-cols-2">
            {(s.includes || []).map((i) => <li key={i} className="flex gap-2 text-sm text-[#0B132B]"><Check className="h-4 w-4 shrink-0 text-gold" /> {i}</li>)}
          </ul>
          <p className="mt-10 text-xs text-slate-400">General visual field service. Not a licensed home inspection, property valuation, or municipal code inspection unless explicitly stated.</p>
        </div>
        <aside className="h-fit rounded-3xl border border-slate-200 bg-white p-8 lg:sticky lg:top-28">
          <p className="font-mono-x text-[10px] uppercase tracking-widest text-gold">Pricing</p>
          <p data-testid="fs-detail-price" className="mt-2 font-display text-3xl font-bold text-[#0B132B]">{priceLabel(s)}</p>
          <CtaButton to={requestLink(s)} size="lg" className="mt-6 w-full justify-center" data-testid="fs-detail-request">Request Service</CtaButton>
          <CtaButton to="/request-inspection" variant="slate" className="mt-3 w-full justify-center" data-testid="fs-detail-quote">Get a Quote</CtaButton>
        </aside>
      </div>
    </section>
  );
}

export function RequestInspection() {
  return (
    <div data-testid="request-inspection-page">
      <section className="bg-[#F5F3EC] py-14">
        <div className="mx-auto max-w-4xl px-4 lg:px-8">
          <Eyebrow>Request an Inspection</Eyebrow>
          <h1 className="mt-3 font-display text-4xl font-extrabold text-[#0B132B] sm:text-5xl">Request Inspection / Get a Quote</h1>
          <p className="mt-4 text-slate-600">Tell us about the property and the service you need. We'll review it and reply with a quote or a scheduled time.</p>
          <div className="mt-10"><RequestForm /></div>
        </div>
      </section>
      <ServiceAreaMap />
    </div>
  );
}
